/**
 * M-A2 — Edit Skill identity modal.
 *
 * Sprint 131 Stream C. Edits display_name, description, vertical, function,
 * category, tags, is_beta. Does NOT touch instructions, activation, or
 * dependencies (those have dedicated modals/tabs).
 */

import { useEffect, useRef, useState } from 'react';
import { DiscardChangesDialog } from '@vanguard-atlas/shared';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { usePatchAdminSkill } from '@/features/skills';
import type { Skill, SkillUpdate } from '@/features/skills';
import { useI18n } from '@/i18n';
import { getErrorMessage } from '@/lib/api-client';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  skill: Skill;
}

export function SkillEditIdentityModal({ open, onOpenChange, skill }: Props) {
  const [displayName, setDisplayName] = useState(skill.display_name);
  const [description, setDescription] = useState(skill.description ?? '');
  const [vertical, setVertical] = useState(skill.vertical);
  const [funcVal, setFuncVal] = useState(skill.function);
  const [category, setCategory] = useState(skill.category);
  const [tagsStr, setTagsStr] = useState(skill.tags.join(', '));
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  // ADR-114 enmienda 1: baseline (valores iniciales desde la skill).
  const initialRef = useRef<string>(
    JSON.stringify({
      displayName: skill.display_name,
      description: skill.description ?? '',
      vertical: skill.vertical,
      funcVal: skill.function,
      category: skill.category,
      tagsStr: skill.tags.join(', '),
    }),
  );
  const isDirty =
    JSON.stringify({ displayName, description, vertical, funcVal, category, tagsStr }) !==
    initialRef.current;
  const discardLabels = {
    title: 'Discard changes?',
    message: 'You have unsaved changes. If you close now, they will be lost.',
    confirm: 'Discard',
    cancel: 'Keep editing',
  };
  const handleClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onOpenChange(false);
  };
  const [isBeta, setIsBeta] = useState(skill.is_beta);

  const { t } = useI18n();

  const patch = usePatchAdminSkill(skill.id);

  useEffect(() => {
    if (open) {
      setDisplayName(skill.display_name);
      setDescription(skill.description ?? '');
      setVertical(skill.vertical);
      setFuncVal(skill.function);
      setCategory(skill.category);
      setTagsStr(skill.tags.join(', '));
      setIsBeta(skill.is_beta);
    }
  }, [open, skill]);

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    const payload: SkillUpdate = {
      display_name: displayName.trim() || null,
      description: description.trim() || null,
      vertical: vertical.trim() || null,
      function: funcVal.trim() || null,
      category: category.trim() || null,
      tags: tagsStr
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      is_beta: isBeta,
    };
    try {
      await patch.mutateAsync(payload);
      toast.success('Skill updated');
      onOpenChange(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) { handleClose(); } }}>
      <DialogContent
        className="max-w-xl"
        dirty={isDirty}
        discardTitle={discardLabels.title}
        discardMessage={discardLabels.message}
        discardConfirmLabel={discardLabels.confirm}
        discardCancelLabel={discardLabels.cancel}
      >
        <DialogHeader>
          <DialogTitle>{t('admin.skills.editIdentity.title')}</DialogTitle>
          <DialogDescription>
            {t('admin.skills.editIdentity.dialogDescription')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label>{t('admin.skills.editIdentity.displayName')}</Label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>{t('admin.skills.editIdentity.descriptionLabel')}</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>{t('admin.skills.editIdentity.vertical')}</Label>
              <Input
                value={vertical}
                onChange={(e) => setVertical(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>{t('admin.skills.editIdentity.skillFunction')}</Label>
              <Input
                value={funcVal}
                onChange={(e) => setFuncVal(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>{t('admin.skills.editIdentity.category')}</Label>
              <Input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label>{t('admin.skills.editIdentity.tags')}</Label>
            <Input
              value={tagsStr}
              onChange={(e) => setTagsStr(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isBeta}
              onChange={(e) => setIsBeta(e.target.checked)}
            />
            {t('admin.skills.editIdentity.markAsBeta')}
          </label>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={patch.isPending}>
              {patch.isPending ? t('admin.skills.editIdentity.saving') : t('admin.skills.editIdentity.saveChanges')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* ADR-114 enmienda 1: confirmación de descarte */}
      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          onOpenChange(false);
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
        title={discardLabels.title}
        message={discardLabels.message}
        confirmLabel={discardLabels.confirm}
        cancelLabel={discardLabels.cancel}
      />
    </Dialog>
  );
}
